import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import React, { useContext, useEffect, useRef, VFC } from 'react';
import Linkify from 'react-linkify';

import { V2CreationData } from '@/apis/aniftyBackend/creations/interface';
import AuthenticityProofBox from '@/components/AuthenticityProofBox';
import CollapsableBox from '@/components/CollapsableBox';
import FramedIcon from '@/components/FramedIcon';
import routes from '@/config/routes';
import { CreationStatus } from '@/utils/getCreationStatus';

import styles from './CreationInfoSection.module.scss';
import MarketplaceItemsContext from '@/contexts/MarketplaceItemsContext';
import router from 'next/router';

type Props = {
  creation: V2CreationData;
  creationStatus: CreationStatus;
};

const CreationInfoSection: VFC<Props> = ({ creation, creationStatus }) => {
  const { i18n } = useTranslation();
  const { searchConditionNFTs, setSearchConditionNFTs } = useContext(
    MarketplaceItemsContext
  );

  const isJA = i18n.language === 'ja';

  const creatorLink = creation.creator.userName
    ? routes.creatorDetail(creation.creator.userName)
    : routes.myPage(creation.creator.userID);

  const description = (isJA && creation.descriptionJA) || creation.description;

  return (
    <div className={styles.creationInfo}>
      {/* Creation Name */}
      <div>
        <div className={styles.header}>
          <h1 className={styles.creationName}>
            {(isJA && creation.titleJA) || creation.title}
          </h1>
        </div>
        <div className={styles.creator}>
          Creator:
          <Link href={creatorLink}>
            <a>
              <FramedIcon
                imageURL={creation.creator.profileImageURL}
                size={25}
                certified
              />
              {(isJA && creation.creator.displayNameJA) ||
                creation.creator.displayName}
            </a>
          </Link>
        </div>
      </div>

      {/* Description */}
      <CollapsableBox heading={description}>
        <p className={styles.creationDescription}>
          <Linkify
            componentDecorator={(decoratedHref, decoratedText, key) => (
              <a
                target="blank"
                rel="noopener noreferrer"
                href={decoratedHref}
                key={key}
              >
                {decoratedText}
              </a>
            )}
          >
            {description}
          </Linkify>
        </p>
      </CollapsableBox>

      <div>
        {creation.tags.map((tag, index) => (
          <a
            key={`tag_${index}`}
            target="_blank"
            href={routes.marketplaceWithTags({
              tagName: tag.name,
              tagNameJA: tag.nameJA,
            })}
            className={styles.creationTag}
          >
            {`#${isJA ? tag.nameJA : tag.name}`}
          </a>
        ))}
      </div>

      {creationStatus !== 'notListed' && (
        <AuthenticityProofBox
          creation={creation}
          tokenID={creation?.aniftyTokenID || 0}
        />
      )}
    </div>
  );
};

export default CreationInfoSection;
